import { useEffect, useState } from "react";
import { Bot, Radar, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "boursicote.intro.dismissed.v1";

/**
 * IntroBanner — a one-time, dismissable explainer shown on the home page.
 * Three concise bullets describe what Boursicote does. Dismissal is
 * persisted in `localStorage` so the banner only shows up once per device.
 */
export function IntroBanner() {
  // Default = hidden until we've inspected localStorage on the client.
  // This avoids a flash on first paint for returning visitors who already
  // dismissed it.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY) === "1";
      if (!dismissed) setVisible(true);
    } catch {
      // localStorage blocked → show it anyway, it's not critical.
      setVisible(true);
    }
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Présentation de Boursicote"
      className="relative overflow-hidden rounded-lg border border-border bg-surface px-5 py-4 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-primary"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Fermer le bandeau"
        title="Ne plus afficher"
        className="absolute top-2 right-2 inline-flex size-7 items-center justify-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors"
      >
        <X className="size-4" />
      </button>

      <div className="flex flex-col gap-2 pr-8">
        <h2 className="text-sm font-semibold text-foreground">
          Boursicote, en bref
        </h2>
        <p className="text-sm text-foreground-muted leading-relaxed">
          Nous indexons en continu les forums Boursorama, en priorisant
          ceux où les conversations sont les plus vives. Chaque message
          est ensuite analysé par IA pour faire ressortir l'essentiel —
          signaux d'achat ou de vente, rumeurs, alertes — sans le bruit.
        </p>

        <ul className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2">
          <Feature icon={Radar} text="Scan ciblé des forums actifs" />
          <Feature icon={Bot} text="Synthèse IA des publications" />
          <Feature
            icon={Sparkles}
            text="Bientôt : interrogez les forums en langage naturel"
            soon
          />
        </ul>

        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={dismiss}>
            J'ai compris
          </Button>
        </div>
      </div>
    </div>
  );
}

function Feature({
  icon: Icon,
  text,
  soon,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
  soon?: boolean;
}) {
  return (
    <li className="flex items-center gap-2 text-xs text-foreground-muted">
      <span className="inline-flex size-6 items-center justify-center rounded bg-primary/10 text-primary shrink-0">
        <Icon className="size-3.5" />
      </span>
      <span>{text}</span>
      {soon && (
        <span className="inline-flex items-center rounded bg-warning/15 text-warning text-[10px] font-medium px-1.5 py-0.5 uppercase tracking-wide">
          Bientôt
        </span>
      )}
    </li>
  );
}
